import styled from 'styled-components'
import { SPACING, BORDER_RADIUS, COLORS, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'

export const CenterBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  background: var(--color-bg-primary);
`

export const ErrorMessage = styled.p`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.body.fontSize};
  text-align: center;
`

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${SPACING.xl};
  color: var(--color-text-primary);
`

export const Content = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-bottom: 100px;
`

export const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm};
  flex: 1;
  min-width: 0;
`

export const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const BrandLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
`

export const PageTitle = styled.h1`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.3;
`

export const TimerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${BORDER_RADIUS.full};
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  margin-top: 2px;
`

export const NameInputWrapper = styled.div`
  margin-bottom: ${SPACING.xl};
`

export const NameInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 ${SPACING.md};
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-primary);
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-text-disabled, var(--color-text-tertiary));
  }
`

export const SectionHeading = styled.div`
  margin: ${SPACING.xl} 0 ${SPACING.md};
  padding-bottom: ${SPACING.sm};
  border-bottom: 1px solid var(--color-border);

  &:first-of-type {
    margin-top: 0;
  }
`

export const SectionLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
`

export const SectionTitle = styled.h2`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
`

export const QuestionCard = styled.div<{ $answered: boolean }>`
  background: var(--color-bg-card);
  border: 1px solid
    ${({ $answered }) => ($answered ? 'var(--color-primary)' : 'var(--color-border)')};
  border-radius: ${BORDER_RADIUS.xl};
  padding: ${SPACING.xl};
  margin-bottom: ${SPACING.md};
  transition: border-color 0.2s ease;
`

export const QuestionMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACING.lg};
`

export const QuestionIndex = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const AnsweredDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary);
  opacity: 0.7;
`

export const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: color-mix(in srgb, var(--color-bg-primary) 85%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--color-border);
  padding: ${SPACING.md} ${SPACING.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.xs};
`

export const FooterInner = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const ProgressText = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
`

export const FooterActions = styled.div`
  display: flex;
  gap: ${SPACING.sm};
  width: 100%;
`

export const ClearButton = styled.button`
  flex: 1;
  height: 44px;
  border-radius: ${BORDER_RADIUS.lg};
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: var(--color-text-primary);
    border-color: var(--color-text-tertiary);
  }
`

export const SaveButton = styled.button<{ $saving: boolean }>`
  flex: 2;
  height: 44px;
  border-radius: ${BORDER_RADIUS.lg};
  border: none;
  background: ${({ $saving }) => ($saving ? 'var(--color-border-dark)' : COLORS.primary)};
  color: #ffffff;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  cursor: ${({ $saving }) => ($saving ? 'not-allowed' : 'pointer')};
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${COLORS.primaryHover};
  }
`

export const SimulateButton = styled.button<{ $loading: boolean }>`
  flex: 1;
  height: 44px;
  border-radius: ${BORDER_RADIUS.lg};
  border: 1px dashed var(--color-border);
  background: transparent;
  color: ${({ $loading }) =>
    $loading ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)'};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  cursor: ${({ $loading }) => ($loading ? 'not-allowed' : 'pointer')};
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    color: var(--color-text-primary);
    border-color: var(--color-text-tertiary);
  }
`

export const ProbeSection = styled.div`
  margin-top: ${SPACING.md};
  padding-top: ${SPACING.md};
  border-top: 1px solid var(--color-border);
`

export const ProbeLabel = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${SPACING.xs};
`

export const ProbeList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const ProbeItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.xs};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.5;

  &::before {
    content: '↳';
    color: var(--color-text-tertiary);
    flex-shrink: 0;
    margin-top: 1px;
  }
`
